<template>
  <div>
    <b-row class="border-bottom mb-2">
      <b-col>
        <h3 class="mb-0">
          Select Device Manufacturer & Model
        </h3>
        <small class="text-muted">
          PRYM continuously adds devices to its list. Can't find your device? Please contact us
        </small>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col>
        <b-form-input
          v-model="search"
          type="search"
          placeholder="Search for a Device Model"
          @input="filteredDeviceModels"
        />
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col>
        <vSelect
          v-model="selectedManufacturer"
          :options="deviceManufacturers"
          placeholder="Select a Manufacturer"
        />
      </b-col>
    </b-row>
    <validation-provider
      v-slot="{ errors }"
      ref="networkValidator"
      :rules="{ model_selected: selectedModel }"
    >
      <b-row>
        <b-col
          v-for="model in filteredDeviceModels"
          :key="model.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          class="mb-4"
        >
          <b-card
            class="h-100 cursor-pointer"
            :class="selectedClass(model)"
            no-body
            @click.stop="selectModel(model)"
          >
            <b-row class="d-flex justify-content-end pr-2 pt-1">
              <feather-icon
                v-if="selectedModel === model"
                icon="CheckCircleIcon"
                class="text-success"
              />
            </b-row>
            <b-card-header
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 class="mb-0">
                  {{ model.name }}
                </h5>
              </div>
            </b-card-header>
            <b-card-body
              class="d-flex justify-content-center align-items-center"
            >
              <b-img
                v-if="model.imgLink"
                alt="Image"
                :src="model.imgLink"
                height="256"
              />
            </b-card-body>
            <b-card-footer class="text-center">
              <b-button
                :variant="selectedModel === model ? 'success' : 'primary'"
                @click.stop="selectModel(model)"
              >
                {{ selectedModel === model ? "Selected" : "Select" }}
              </b-button>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-form-invalid-feedback :state="!errors[0]">
        {{ errors[0] }}
      </b-form-invalid-feedback>
    </validation-provider>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BImg,
  BCard,
  BCardHeader,
  BCardBody,
  BCardFooter,
  BFormInvalidFeedback,
  BButton,
  BFormInput,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { API } from 'aws-amplify'
import { ValidationProvider } from 'vee-validate'

import * as queries from '../../../graphql/queries'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardFooter,
    BRow,
    BCol,
    BImg,
    ValidationProvider,
    BFormInvalidFeedback,
    BButton,
    BFormInput,
    vSelect,
  },
  props: {
    newDevice: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      deviceManufacturers: [],
      selectedManufacturer: '',
      selectedModel: '',
      selectedDevice: '',
      deviceModels: [],
      modelInfo: [],
      modelInfoFields: [
        { key: 'deviceManufacturer.name', label: 'Manufacturer' },
        { key: 'name', label: 'Model Name' },
        { key: 'modelNumber', label: 'Model Number' },
      ],
      manufacturerModels: [],
      sensors: [],
      sensorFields: [
        { key: 'name', label: 'Sensor Name', sortable: true },
        { key: 'identifier', label: 'Identifier', sortable: true },
        { key: 'description', label: 'Description', sortable: true },
      ],
    }
  },
  computed: {
    filteredDeviceModels() {
      if (!this.search) {
        return this.deviceModels
      }
      return this.deviceModel.filter(
        deviceModel => deviceModel.name
          .toLowerCase()
          .includes(this.search.toLowerCase())
          || deviceModel.modelNumber
            .toLowerCase()
            .includes(this.search.toLowerCase()),
      )
    },
  },

  watch: {
    async selectedManufacturer(newVal) {
      if (newVal === null) {
        this.selectedModel = ''
        this.sensors = []
      } else {
        this.newDevice.deviceInfo.deviceManufacturer = newVal.name
        await API.graphql({
          query: queries.listDeviceModels,
          filter: { deviceManufacturerDeviceModelsId: { eq: this.selectedManufacturer.id } },
        }).then(response => {
          this.manufacturerModels = response.data.listDeviceModels.items
        })
      }
    },
    selectedModel: {
      deep: true,
      async handler(newVal) {
        if (newVal === null) {
          this.selectedModel = ''
          this.sensors = []
        } else if (newVal.id) {
          this.sensor = []
          this.modelInfo.push(newVal)

          this.newDevice.deviceInfo.deviceModelID = newVal.id
          this.newDevice.deviceInfo.deviceModelName = newVal.name
          this.newDevice.deviceInfo.deviceModelNumber = newVal.modelNumber
          this.newDevice.deviceInfo.imgLink = newVal.imgLink
          await API.graphql({
            query: queries.listDeviceModelSensors,
            variables: {
              filter: { deviceModelID: { eq: newVal.id } },
              limit: 1000,
            },
          }).then(async response => {
            await response.data.listDeviceModelSensors.items.forEach(item => { this.sensors.push(item.sensor) })
            this.newDevice.deviceInfo.sensors = this.sensors
          })
        }
      },
    },
  },
  created() {
    this.listDeviceManModel()
  },
  methods: {
    async listDeviceManModel() {
      await API.graphql({
        query: queries.listDeviceModels,
      }).then(response => {
        this.deviceModels = response.data.listDeviceModels.items
      })
    },
    async getSenorsForDeviceModel(deviceModelID) {
      await API.graphql({
        query: queries.listDeviceModelSensors,
        filter: { deviceModelID: { eq: deviceModelID } },
      }).then(response => {
        this.sensors = response.data.listDeviceModelSensors.items
      })
    },
    selectModel(model) {
      this.$emit('selectedModel', model)
      this.selectedModel = model
    },
    selectedClass(device) {
      return this.selectedDevice === device ? 'border-success' : ''
    },
    rowClass(item, index) {
      if (this.selectedDevice === item) {
        return 'bg-success'
      }
      if (index % 2 === 0) {
        return 'bg-light'
      }
      return ''
    },
    networkTooltip() {
      return 'Click to select the network for your device'
    },
  },
}
</script>

<style>

</style>
